import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { MyListing, UserMarketplaceStats } from '@/types/residents/marketplaceListing';
import { 
  BreadcrumbNavigation, 
  MarketplaceScreenLayout,
  ProfileStatistics,
  MyListingCard,
  EmptyListingsState,
  ProfileNavDropdown,
  FeedbackModal,
  ToggleBar,
} from '@/components/residents/marketplace';
import { 
  MARKETPLACE_MAX_CONTENT_WIDTH,
  getResponsivePadding,
} from '@/utils/marketplace';
import { useGetMyProfileQuery, useGetMyListingsQuery, useGetReviewsQuery } from '@/store/api';

type MarketplaceProfileNavigationProp = StackNavigationProp<RootStackParamList, 'MarketplaceProfile'>;
type MarketplaceProfileRouteProp = RouteProp<RootStackParamList, 'MarketplaceProfile'>;

type ListingTab = 'active' | 'sold';

// Default stats when loading
const DEFAULT_STATS: UserMarketplaceStats = {
  bought: 0,
  sold: 0,
  followers: 0,
  following: 0,
  rating: 0,
  reviewCount: 0,
};

export const MarketplaceProfileScreen: React.FC = () => {
  const navigation = useNavigation<MarketplaceProfileNavigationProp>();
  const route = useRoute<MarketplaceProfileRouteProp>();
  const { layoutMode, isWeb } = useResponsive();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const contentPadding = getResponsivePadding(layoutMode);
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;
  
  const [activeTab, setActiveTab] = useState<ListingTab>('active');
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);

  // Fetch profile stats from API
  const { 
    data: userStats = DEFAULT_STATS, 
    isLoading: isLoadingProfile,
  } = useGetMyProfileQuery();

  // Fetch listings from API (fetch all, filter client-side for tab switching)
  const { 
    data: myListings = [], 
    isLoading: isLoadingListings,
    refetch: refetchListings,
  } = useGetMyListingsQuery({ status: 'all' });

  // Fetch reviews from API
  const { 
    data: feedbackItems = [], 
    isLoading: isLoadingReviews,
  } = useGetReviewsQuery({});

  const isLoading = isLoadingProfile || isLoadingListings;

  // Handle action params from detail screen
  useEffect(() => {
    const params = route.params;
    if (params?.action && params?.listingId) {
      const { action } = params;
      
      // Refetch listings after action
      refetchListings();
      
      if (action === 'sold') {
        setActiveTab('sold');
      } else if (action === 'reposted') {
        setActiveTab('active');
      }
      
      // Clear the params after handling
      navigation.setParams({ action: undefined, listingId: undefined });
    }
  }, [route.params, refetchListings]);

  // Filter listings by tab
  const filteredListings = useMemo(() => {
    return myListings.filter(listing => listing.status === activeTab);
  }, [myListings, activeTab]);

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToMarketplace = () => {
    if (isWeb && isDesktop) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  };

  const handleListingPress = (listing: MyListing) => {
    navigation.navigate('MyListingItemDetail', { listing });
  };

  const handleFollowingPress = () => {
    navigation.navigate('MarketplaceFollowing');
  };

  const handleRatingPress = () => {
    setFeedbackModalVisible(true);
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModalVisible(false);
  };

  return (
    <>
      <MarketplaceScreenLayout navigatorTitle="My profile">
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={[
            styles.content,
            { padding: contentPadding },
            !isMobile && styles.contentLarge,
          ]}
          showsVerticalScrollIndicator={false}
        >
          {/* Responsive content wrapper */}
          <View style={[
            styles.contentWrapper,
            maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
          ]}>
            {/* Back Navigation */}
            <BreadcrumbNavigation 
              onHomePress={handleGoToMarketplace}
              onBackPress={handleGoBack}
            />

            {/* Profile Navigation Dropdown */}
            <ProfileNavDropdown activeOption="my_profile" />

            {/* Statistics Section */}
            <ProfileStatistics 
              stats={userStats}
              onFollowingPress={handleFollowingPress}
              onRatingPress={handleRatingPress}
            />

            {/* Listings Section */}
            <View style={styles.listingsSection}>
              <Text style={[
                styles.listingsTitle,
                !isMobile && styles.listingsTitleLarge,
              ]}>Listings</Text>
              
              {/* Toggle Bar */}
              <ToggleBar
                options={[
                  { key: 'active', label: 'Active' },
                  { key: 'sold', label: 'Sold' },
                ]}
                activeKey={activeTab}
                onSelect={(key) => setActiveTab(key as ListingTab)}
                maxWidth={!isMobile ? 300 : undefined}
              />

              {/* Listings */}
              <View style={styles.listingsContainer}>
                {isLoadingListings ? (
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
                  </View>
                ) : filteredListings.length > 0 ? (
                  filteredListings.map((listing) => (
                    <MyListingCard
                      key={listing.id}
                      listing={listing}
                      onPress={() => handleListingPress(listing)}
                    />
                  ))
                ) : (
                  <EmptyListingsState type={activeTab} />
                )}
              </View>
            </View>
          </View>
        </ScrollView>
      </MarketplaceScreenLayout>
      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={handleCloseFeedbackModal}
        feedbackItems={feedbackItems}
      />
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    gap: 24,
  },
  contentLarge: {
    gap: 32,
    paddingBottom: 60,
  },
  contentWrapper: {
    gap: 24,
  },
  // Listings Section
  listingsSection: {
    gap: 24,
  },
  listingsTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  listingsTitleLarge: {
    fontSize: 18,
    lineHeight: 27,
  },
  listingsContainer: {
    gap: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
});
